// Route Code Edited 07/09/2026
/**
 * Route Element Text Handler
 *
 * Watches for configured elements in the DOM and enforces text content
 * via MutationObserver. Re-enforces on any mutation to prevent third-party
 * apps from overwriting Route's element text.
 *
 * Each entry in ELEMENT_CONFIGS targets a selector independently, using one
 * of two modes:
 *
 *   - processText(fullText) => newFullText
 *       Full rebuild via el.textContent = newFullText. Only safe for
 *       elements where ALL child text nodes are static (nothing else is
 *       going to update them later). If the element is rendered by React
 *       (or another framework) and any part of its text is updated
 *       independently after this script runs, a full rebuild destroys the
 *       original text node(s) that framework's internals hold a reference
 *       to, silently breaking that framework's ability to update the
 *       element afterward — the visible DOM stops reflecting new data even
 *       though the framework's internal state is correct.
 *
 *   - processLabelText(fullText) => newLabelNodeText
 *       Node-preserving mode. Rewrites ONLY the element's first text node
 *       in place (via nodeValue, not textContent) and leaves every other
 *       child node completely untouched. Use this when the element has
 *       multiple text nodes (e.g. a static label node followed by a
 *       separately-updating value node) and only the label should change.
 *       This is required for Route's `.pw-coverage-description`, which
 *       renders its label and price as two independent React-managed text
 *       nodes — confirmed via:
 *         document.querySelector('.pw-coverage-description').childNodes
 *       returning two Text nodes, each with its own __reactFiber$ key.
 *
 * If both processText and processLabelText are omitted, the element is
 * watched but never modified.
 *
 * Note: verify the target element's DOM structure (childNodes, not just
 * innerHTML/textContent) before adding a new config entry, so you know
 * which mode applies.
 *
 * Installation:
 *   Add this script tag at the bottom of layout/theme.liquid (before </body>):
 *     <script src="{{ 'route-element-text-handler.js' | asset_url }}" defer></script>
 */
(function routeElementTextHandler() {
  // ─── Configure below ──────────────────────────────────────
  const ELEMENT_CONFIGS = [
    {
      selector: '.pw-coverage-description',
      // Matches the label node's own trimmed text. Route ships more than one
      // wording for this label across its responsive variants — confirmed:
      // "Order protected for" (small variant) and
      // "Your order is protected for" (default variant). Both end in
      // "protected for", so anchor on that rather than one exact phrase.
      textMatchRegex: /protected for$/i,
      processLabelText: () => 'Optional package protection for ',
    },
  ];
  // ────────────────────────────────────────────────────

  function getFirstTextNode(el) {
    for (const node of el.childNodes) {
      if (node.nodeType === Node.TEXT_NODE) return node;
    }
    return null;
  }

  function getMatchText(el, config) {
    // Label mode gates on the label node's own text, since Route ships more
    // than one label wording across responsive variants of this element
    // ("Order protected for..." vs "Your order is protected for...") — the
    // regex needs to match both, and must never be tested against the full
    // element text (which includes the live price and would need updating
    // forever as the price changes).
    if (config.processLabelText != null) {
      const labelNode = getFirstTextNode(el);
      return labelNode ? labelNode.nodeValue.trim() : null;
    }
    return el.textContent.trim();
  }

  function shouldProcessText(el, config) {
    if (config.processText == null && config.processLabelText == null) return false;
    const text = getMatchText(el, config);
    if (text == null) return false;
    if (config.textMatchRegex != null && !config.textMatchRegex.test(text)) return false;
    return true;
  }

  function hasUnwantedState(el, config) {
    if (!shouldProcessText(el, config)) return false;
    const fullText = el.textContent.trim();

    if (config.processLabelText != null) {
      const labelNode = getFirstTextNode(el);
      if (!labelNode) return false;
      return labelNode.nodeValue !== config.processLabelText(fullText);
    }

    return fullText !== config.processText(fullText);
  }

  function applyChanges(el, config, observer) {
    observer.disconnect();

    if (shouldProcessText(el, config)) {
      const fullText = el.textContent.trim();

      if (config.processLabelText != null) {
        // Node-preserving path: only touch the first text node's value.
        const labelNode = getFirstTextNode(el);
        if (labelNode) {
          labelNode.nodeValue = config.processLabelText(fullText);
        }
      } else {
        // Full-rebuild path: only safe for elements with no other party
        // updating any part of their text later.
        el.textContent = config.processText(fullText);
      }
    }

    observer.observe(el, {
      characterData: true,
      subtree: true,
    });
  }

  function watchElement(el, config) {
    if (el._routeHandlerWatched) return;
    el._routeHandlerWatched = true;
    const observer = new MutationObserver(() => {
      if (hasUnwantedState(el, config)) applyChanges(el, config, observer);
    });
    applyChanges(el, config, observer);
  }

  const domObserver = new MutationObserver(() => {
    ELEMENT_CONFIGS.forEach(config => {
      document.querySelectorAll(config.selector).forEach(el => watchElement(el, config));
    });
  });
  domObserver.observe(document.body, { childList: true, subtree: true });
  ELEMENT_CONFIGS.forEach(config => {
    document.querySelectorAll(config.selector).forEach(el => watchElement(el, config));
  });
  window.addEventListener('pagehide', () => domObserver.disconnect());
})();